'use client';

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, RefreshCw, UserRound } from 'lucide-react';
import { toast } from 'sonner';

import { Button, ConfirmDialog, EmptyState, Pill, Skeleton } from '@/components/custom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { gatewayService, type SdkSessionSummary } from '@/lib/api/services/gateway';
import { when } from './format';

type Pending =
  | { kind: 'one'; session: SdkSessionSummary }
  | { kind: 'user'; session: SdkSessionSummary };

export function NarrowedBadge({ grants }: { grants: string[] }) {
  if (!grants.length) return <span className="text-[12px] text-cs-text-muted">Role default</span>;
  return (
    <span title={grants.join('\n')} aria-label={`Narrowed to ${grants.join(', ')}`}>
      <Pill tone="warning">Narrowed ({grants.length})</Pill>
    </span>
  );
}

export default function SdkSessionsTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const queryKey = ['developer-access', projectId, 'sdk-sessions'];
  const sessionsQ = useQuery({ queryKey, queryFn: () => gatewayService.listSdkSessions(projectId) });
  const [pending, setPending] = React.useState<Pending | null>(null);

  const end = useMutation({
    mutationFn: async (p: Pending) => {
      if (p.kind === 'one') {
        await gatewayService.revokeSdkSession(p.session.id, projectId);
        return 1;
      }
      return gatewayService.revokeSdkSessionsForUser(p.session.user_id, projectId);
    },
    onSuccess: (n, p) => {
      const who = p.session.email || p.session.user_id;
      toast.success(p.kind === 'one' ? `Session for ${who} revoked` : `Ended ${n} session${n === 1 ? '' : 's'} for ${who}`);
      qc.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to end the session'),
    onSettled: () => setPending(null),
  });

  const sessions = sessionsQ.data ?? [];
  const who = pending ? pending.session.email || pending.session.user_id : '';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-[12.5px] text-cs-text-muted">
          Users signed in to this project through the SDK with their email and password. Each session acts as that user.
        </p>
        <Button variant="secondary" size="sm" onClick={() => sessionsQ.refetch()} loading={sessionsQ.isFetching}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Refresh
        </Button>
      </div>

      {sessionsQ.isLoading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} variant="card" />)}</div>
      ) : sessionsQ.isError ? (
        <EmptyState
          icon={UserRound}
          title="Failed to load SDK sessions"
          description={sessionsQ.error instanceof Error ? sessionsQ.error.message : 'The gateway is unreachable.'}
          action={<Button variant="secondary" onClick={() => sessionsQ.refetch()}>Retry</Button>}
        />
      ) : sessions.length === 0 ? (
        <EmptyState
          icon={UserRound}
          title="No SDK sessions"
          description="When a user logs in to this project through the SDK, their session appears here."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-cs-border bg-cs-surface-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Grants</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id} data-testid="sdk-session-row">
                  <TableCell className="max-w-[260px]">
                    <div className="truncate text-[13px] text-cs-text-primary" title={s.email || s.user_id}>{s.email || s.user_id}</div>
                    <div className="truncate font-mono text-[11px] text-cs-text-muted">{s.id}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-[12px] text-cs-text-muted">{when(s.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap text-[12px] text-cs-text-muted">{s.last_used_at ? when(s.last_used_at) : 'Never'}</TableCell>
                  <TableCell className="whitespace-nowrap text-[12px] text-cs-text-muted">{when(s.expires_at)}</TableCell>
                  <TableCell><NarrowedBadge grants={s.grants} /></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label={`Actions for ${s.email || s.user_id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setPending({ kind: 'one', session: s })}>
                          Revoke this session
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-cs-error-400" onSelect={() => setPending({ kind: 'user', session: s })}>
                          End all sessions for {s.email || s.user_id}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
        variant="destructive"
        title={pending?.kind === 'user' ? `End all SDK sessions for “${who}”?` : `Revoke this SDK session for “${who}”?`}
        description={
          pending?.kind === 'user'
            ? 'Every SDK session this user has in this project ends now. They can log in again.'
            : 'This session can no longer refresh, and its access token stops working at its next call. The user can log in again.'
        }
        confirmLabel={pending?.kind === 'user' ? 'End all sessions' : 'Revoke session'}
        loading={end.isPending}
        onConfirm={async () => {
          if (pending) await end.mutateAsync(pending).catch(() => undefined);
        }}
      />
    </div>
  );
}
